import { TZDate } from '@date-fns/tz';
import { differenceInCalendarDays, min as minDate, subDays } from 'date-fns';
import { Triple, TripleParams } from '../../shared/types/triple.mts';
import { TUTORIAL_ID } from './Constants.tsx';
import { KeyValueStore } from './KeyValueStore.ts';
import { deepEquals, toGameWideStateKey } from './utils.ts';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/capacitor';

const TIME_ZONE = 'America/New_York';

interface GuessParams {
    tiles: GuessTileParams[];
    maxLength: number;
}

class Guess {
    readonly tiles: Array<GuessTile>;
    readonly maxLength: number;

    constructor(arg?: number | GuessTile[]) {
        if (typeof arg === 'number') {
            this.maxLength = arg;
            this.tiles = Array(arg).fill(new GuessTile('', GuessLetterState.INITIAL)) as GuessTile[];
        } else if (Array.isArray(arg)) {
            this.maxLength = arg.length;
            this.tiles = [...arg];
        }
    }

    toJSON(): GuessParams {
        return {
            tiles: this.tiles.map((tile) => tile.toJSON()),
            maxLength: this.maxLength,
        };
    }

    static from(obj: GuessParams) {
        const tiles = obj.tiles.map((tile) => GuessTile.from(tile));
        return new Guess(tiles);
    }

    full() {
        return this.tiles.every((tile) => !tile.empty());
    }

    empty() {
        return this.tiles.every((tile) => tile.empty());
    }

    verified() {
        return this.tiles.every((tile) => tile.verified());
    }

    partial() {
        return this.checkable() && this.tiles.some((tile) => tile.empty());
    }

    checkable() {
        return this.tiles.some((tile) => tile.state === GuessLetterState.INITIAL && !tile.empty());
    }

    editable() {
        return this.tiles.some((tile) => tile.editable());
    }

    numEditableTiles() {
        return this.tiles.reduce((sum, tile) => {
            return tile.editable() ? sum + 1 : sum;
        }, 0);
    }

    length() {
        return this.tiles.length;
    }

    getTile(i: number): GuessTile {
        return this.tiles[i];
    }

    verifiedAndSpellsWord(word: string) {
        return this.verified() && this.toString() === word;
    }

    turnSolved() {
        return Math.max(...this.tiles.map((tile) => tile.turnSolved));
    }

    append(char: string) {
        // Clear out all incorrect tiles.
        const newTiles = this.tiles.map((tile) => {
            if (tile.state === GuessLetterState.INCORRECT) {
                return new GuessTile('', GuessLetterState.INITIAL);
            }
            return tile;
        });
        // Find the first editable tile that is empty
        for (let i = 0; i < newTiles.length; i++) {
            const tile = newTiles[i];
            if (tile.editable() && tile.empty()) {
                return new Guess([
                    ...newTiles.slice(0, i),
                    new GuessTile(char, GuessLetterState.INITIAL),
                    ...newTiles.slice(i + 1),
                ]);
            }
        }

        // No room for an append.
        return new Guess(newTiles);
    }

    deleteLastLetter() {
        // Clear out all incorrect tiles.
        const newTiles = this.tiles.map((tile) => {
            if (tile.state === GuessLetterState.INCORRECT) {
                return new GuessTile('', GuessLetterState.INITIAL);
            }
            return tile;
        });
        // Find the last editable tile that is non-empty
        for (let i = newTiles.length - 1; i >= 0; i--) {
            const tile = newTiles[i];
            if (tile.editable() && !tile.empty()) {
                return new Guess([
                    ...newTiles.slice(0, i),
                    new GuessTile('', GuessLetterState.INITIAL),
                    ...newTiles.slice(i + 1),
                ]);
            }
        }

        // No letters to delete.
        return new Guess(newTiles);
    }

    clear() {
        return new Guess(
            this.tiles.map((tile, i) => {
                if (tile.editable() && !tile.empty()) {
                    return new GuessTile('', GuessLetterState.INITIAL);
                } else {
                    return tile;
                }
            })
        );
    }

    markFailures() {
        return new Guess(
            this.tiles.map((tile) => {
                if (!tile.verified()) {
                    return new GuessTile(tile.char, GuessLetterState.FAILED);
                }
                return tile;
            })
        );
    }

    revealDueToFailure(answer: string) {
        return new Guess(
            answer.split('').map((answerLetter, i) => {
                if (!this.tiles[i].verified()) {
                    return new GuessTile(answerLetter, GuessLetterState.FAILED);
                }
                return this.tiles[i];
            })
        );
    }

    revealNextLetter(answer: string, turn: number) {
        let revealedLetter = false;
        // First pass: reveal the first blank tile if one exists.
        let newTiles = this.tiles.map((tile, i) => {
            if (!revealedLetter && tile.editable() && tile.char === '') {
                revealedLetter = true;
                return new GuessTile(answer[i], GuessLetterState.REVEALED, turn);
            } else {
                return tile;
            }
        });
        // Second pass: reveal the first editable tile.
        if (!revealedLetter) {
            newTiles = this.tiles.map((tile, i) => {
                if (!revealedLetter && tile.editable()) {
                    revealedLetter = true;
                    return new GuessTile(answer[i], GuessLetterState.REVEALED, turn);
                } else {
                    return tile;
                }
            });
        }
        return new Guess(newTiles);
    }

    copy(): Guess {
        return new Guess([...this.tiles]);
    }

    toString() {
        return this.tiles.map((tile) => tile.char).join('');
    }

    toStringExcludeFailures() {
        return this.tiles
            .map((tile) => {
                if (tile.state === GuessLetterState.FAILED) {
                    return ' ';
                }
                return tile.char;
            })
            .join('');
    }
}

enum GuessLetterState {
    INITIAL,
    COVERED_TRIPLET_LETTER,
    REVEALED,
    CORRECT,
    INCORRECT,
    FAILED,
}

interface GuessTileParams {
    char: string;
    state: GuessLetterState;
    turnSolved: number;
}

class GuessTile {
    readonly char: string;
    readonly state: GuessLetterState;
    readonly turnSolved: number;

    constructor(char: string, state: GuessLetterState, turnSolved: number = -1) {
        if (char.length > 1) {
            throw new Error('GuessEntry.setChar: char must be either zero or one characters');
        }
        this.char = char;
        this.state = state;
        this.turnSolved = turnSolved == null ? -1 : turnSolved;
    }

    toJSON(): GuessTileParams {
        return {
            char: this.char,
            state: this.state,
            turnSolved: this.turnSolved,
        };
    }

    static from(params: GuessTileParams) {
        return new GuessTile(params.char, params.state, params.turnSolved);
    }

    verified() {
        return this.state === GuessLetterState.REVEALED || this.state === GuessLetterState.CORRECT;
    }

    editable() {
        return this.state === GuessLetterState.INITIAL || this.state === GuessLetterState.INCORRECT;
    }

    empty() {
        return this.char === '';
    }

    incorrect() {
        return this.state === GuessLetterState.INCORRECT;
    }
}

/* Do NOT change the order of this enum! It is persisted in user's localStorage numerically. */
enum GameState {
    IN_PROGRESS,
    COMPLETED /* deprecated */,
    UNKNOWN,
    WON,
    LOST,
}

export enum LoadingState {
    LOADING,
    ERROR,
    LOADED,
}

export function practiceTriplit() {
    return new Triple({
        id: TUTORIAL_ID,
        triplet: 'tse',
        entries: [
            { clue: 'Untamed being', answer: 'beast' },
            { clue: 'Dines', answer: 'eats' },
            { clue: 'Not wobbly', answer: 'stable' },
        ],
    });
}

function isCompleted(state: GameState | undefined) {
    return state === GameState.COMPLETED || state === GameState.WON || state === GameState.LOST;
}

/* Do NOT change the order of this enum! It is persisted in user's localStorage and in the database numerically. */
enum TurnType {
    REVEAL,
    SUBMIT,
}

function scoreToCssClass(score: number, fill: boolean = false, outline: boolean = false, border: boolean = false) {
    const suffix = `${fill ? '-fill' : outline ? '-outline' : border ? '-border' : ''}`;
    switch (score) {
        case 1:
            return `first-turn${suffix}`;
        case 2:
            return `second-turn${suffix}`;
        case 3:
            return `third-turn${suffix}`;
        case 4:
            return `fourth-turn${suffix}`;
        case 5:
            return `fifth-turn${suffix}`;
        default:
            return '';
    }
}

enum Directions {
    NONE = 0,
    LEFT = -1,
    RIGHT = 1,
}

/* Do NOT modify existing values of this enum! It is persisted in user's localStorage and in the database numerically. */
enum DifficultyLevel {
    EASY = 0,
    NORMAL = 1,
    HARD = 2,
}

/* Do NOT modify existing values of this enum! It is persisted in user's localStorage. */
enum DarkModeSetting {
    SYSTEM = 0,
    LIGHT = 1,
    DARK = 2,
}

class ArchiveSelector {
    date: Date;
    constructor(date: Date) {
        this.date = date;
    }

    targetPath() {
        if (this.isToday()) {
            return '/';
        } else {
            return this.toArchiveUrl();
        }
    }

    isToday() {
        const now = new TZDate(new TZDate(), TIME_ZONE);
        return differenceInCalendarDays(this.date, now) === 0;
    }

    toArchiveUrl() {
        return `/archive/${this.date.getFullYear()}/${this.date.getMonth() + 1}/${this.date.getDate()}`;
    }
}

class BonusSelector {
    id: number;
    constructor(id: number) {
        this.id = id;
    }

    targetPath() {
        return `/bonus/${this.id}`;
    }
}

class InternalSelector {
    readonly id: number | null;
    constructor(id: number | null) {
        this.id = id;
    }

    targetPath() {
        if (this.id == null) {
            return `/beta/random`;
        } else {
            return `/beta/${this.id}`;
        }
    }
}

class SpecialSelector {
    id: number;
    constructor(id: number) {
        this.id = id;
    }

    targetPath() {
        return `/special/${this.id}`;
    }
}

type SelectedPuzzle = ArchiveSelector | BonusSelector | InternalSelector | SpecialSelector;

export class PuzzleKey {
    constructor(public readonly id: string, public readonly userId?: string) {}

    toString(): string {
        return `triplit_${this.id}${this.userId ? `_${this.userId}` : ''}`;
    }

    static parse(key: string): PuzzleKey | undefined {
        const match = key.match(/^triplit_([^_]+?)(?:_(.+?))?$/);
        if (!match) return undefined;
        const id = match[1];
        const userId = match[2] ?? undefined;
        return new PuzzleKey(id, userId);
    }
}

export class TimeElapsedKey {
    private puzzleKey: PuzzleKey;
    constructor(public readonly id: string, public readonly userId?: string) {
        this.puzzleKey = new PuzzleKey(id, userId);
    }

    toString(): string {
        return `${this.puzzleKey.toString()}_timeElapsed`;
    }
}

interface BasicFieldParams {
    lastModifiedMs?: number;
}

interface FieldParams<V> extends BasicFieldParams {
    rawValue: V;
}

abstract class Field<T, V = T> implements FieldParams<T | V> {
    rawValue: V | T;
    protected value: T;
    readonly lastModifiedMs: number;
    constructor(obj: FieldParams<T | V>) {
        if (obj.rawValue == null) {
            throw new Error('Field: rawValue is required');
        }
        this.lastModifiedMs = obj.lastModifiedMs ?? Date.now();
    }

    static create<T, V>(
        this: new (obj: FieldParams<T | V>) => Field<T, V>,
        obj: FieldParams<T | V>
    ): InstanceType<typeof this> | undefined {
        try {
            return new this(obj);
        } catch (e) {
            console.error('Error creating field. Falling back to undefined. Error=', e);
            Sentry.captureException(e);
            return undefined;
        }
    }

    toJSON(): FieldParams<T | V> {
        return {
            rawValue: this.getSerializableValue(),
            lastModifiedMs: this.lastModifiedMs,
        };
    }

    abstract getSerializableValue(): V;

    getValue(): T {
        return this.value;
    }

    getOrDefault(defaultValue: T): T {
        return this.value ?? defaultValue;
    }
}

export class StringField extends Field<string> {
    constructor(obj: FieldParams<string>) {
        super(obj);
        this.value = obj.rawValue;
        if (typeof obj.rawValue !== 'string') {
            throw new Error('Invalid string format: ' + String(obj.rawValue));
        }
    }

    getSerializableValue(): string {
        return this.value;
    }
}

export class BooleanField extends Field<boolean> {
    constructor(obj: FieldParams<boolean>) {
        super(obj);
        this.value = obj.rawValue;
        if (typeof obj.rawValue !== 'boolean') {
            throw new Error('Invalid boolean format: ' + String(obj.rawValue));
        }
    }
    getSerializableValue(): boolean {
        return this.value;
    }
}

export class DateField extends Field<Date, string> {
    constructor(obj: FieldParams<Date | string>) {
        super(obj);
        this.value = new Date(obj.rawValue);
        if (isNaN(this.value.getTime())) {
            throw new Error('Invalid date format: ' + String(obj.rawValue));
        }
    }

    getSerializableValue(): string {
        return this.value.toISOString();
    }
}

export class NumberField extends Field<number> {
    constructor(obj: FieldParams<number>) {
        super(obj);
        this.value = obj.rawValue;
        if (isNaN(this.value)) {
            throw new Error('Invalid number format: ' + String(obj.rawValue));
        }
    }

    getSerializableValue(): number {
        return this.value;
    }
}

export class DifficultyLevelField extends Field<DifficultyLevel> {
    constructor(obj: FieldParams<DifficultyLevel>) {
        super(obj);
        this.value = obj.rawValue;
        const validValues = Object.values(DifficultyLevel);
        if (!validValues.includes(this.value)) {
            throw new Error(`Invalid DifficultyLevel value: ${String(obj.rawValue)}`);
        }
    }
    getSerializableValue(): number {
        return this.value;
    }
}

export class DarkModeSettingField extends Field<DarkModeSetting> {
    constructor(obj: FieldParams<DarkModeSetting>) {
        super(obj);
        this.value = obj.rawValue;
        const validValues = Object.values(DarkModeSetting);
        if (!validValues.includes(this.value)) {
            throw new Error(`Invalid DarkModeSetting value: ${String(obj.rawValue)}`);
        }
    }
    getSerializableValue(): DarkModeSetting {
        return this.value;
    }
}

export class ObjectField extends Field<object> {
    constructor(obj: FieldParams<object>) {
        super(obj);
        this.value = obj.rawValue;
        if (typeof obj.rawValue !== 'object' || Array.isArray(obj.rawValue)) {
            throw new Error('Invalid object format: ' + String(obj.rawValue));
        }
    }
    getSerializableValue(): object {
        return this.value;
    }
}

export class NumberSetField extends Field<Set<number>, Array<number>> {
    constructor(obj: FieldParams<Array<number> | Set<number>>) {
        super(obj);
        this.value = new Set(obj.rawValue);
        if (!Array.from(this.value).every((n) => typeof n === 'number' && !isNaN(n))) {
            throw new Error('Invalid number set format: ' + JSON.stringify(obj.rawValue));
        }
    }
    getSerializableValue(): number[] {
        return Array.from(this.value);
    }
}

enum DeltaTypes {
    RECORD_WIN,
    RECORD_LOSS,
    RECORD_BONUS_UNLOCK,
}

class DeltaFactory {
    static from(obj: { id: string; type: DeltaTypes; turns?: number }): Delta | undefined {
        const type = obj.type;
        switch (type) {
            case DeltaTypes.RECORD_WIN:
                if (obj.turns == null) {
                    console.error('DeltaFactory: Missing turns for RECORD_WIN delta');
                    return undefined;
                }
                return new RecordWinDelta(obj.turns, obj.id);
            case DeltaTypes.RECORD_LOSS:
                return new RecordLossDelta(obj.id);
            case DeltaTypes.RECORD_BONUS_UNLOCK:
                return new RecordBonusUnlockDelta(obj.id);
            default:
                throw new Error(`Unknown delta type: ${obj.type}`);
        }
    }
}

abstract class Delta {
    readonly id: string;
    readonly type: DeltaTypes;
    abstract apply(data: GameWideData): void;
    abstract unapply(data: GameWideData): void;
    constructor(id?: string) {
        this.id = id ?? uuidv4();
    }
}

class RecordWinDelta extends Delta {
    readonly type: DeltaTypes = DeltaTypes.RECORD_WIN;
    readonly turns: number;

    constructor(turns: number, id?: string) {
        super(id);
        this.turns = turns;
    }

    apply(data: GameWideData) {
        data.totalPlayed = new NumberField({ rawValue: (data.totalPlayed?.getValue() ?? 0) + 1 });
        data.totalWins = new NumberField({ rawValue: (data.totalWins?.getValue() ?? 0) + 1 });
        data.scoreDistribution = new ObjectField({
            rawValue: {
                ...(data.scoreDistribution?.getValue() ?? {}),
                [this.turns]: ((data.scoreDistribution?.getValue()[this.turns] ?? 0) as number) + 1,
            },
        });
    }

    unapply(data: GameWideData) {
        data.totalPlayed = new NumberField({ rawValue: (data.totalPlayed?.getValue() ?? 0) - 1 });
        data.totalWins = new NumberField({ rawValue: (data.totalWins?.getValue() ?? 0) - 1 });
        if (data.scoreDistribution == null) {
            throw new Error('Unapplying win from record with an undefined scoreDistribution');
        }
        data.scoreDistribution = new ObjectField({
            rawValue: {
                ...data.scoreDistribution.getValue(),
                [this.turns]: (data.scoreDistribution.getValue()[this.turns] || 0) - 1,
            },
        });
        if (data.totalPlayed.getValue() < 0 || data.totalWins.getValue() < 0) {
            throw new Error('Unapplying win delta would result in negative totalPlayed');
        }
    }
}

class RecordLossDelta extends Delta {
    readonly type: DeltaTypes = DeltaTypes.RECORD_LOSS;

    constructor(id?: string) {
        super(id);
    }

    apply(data: GameWideData) {
        data.totalPlayed = new NumberField({ rawValue: (data.totalPlayed?.getValue() ?? 0) + 1 });
        data.totalLosses = new NumberField({ rawValue: (data.totalLosses?.getValue() ?? 0) + 1 });
    }

    unapply(data: GameWideData) {
        data.totalPlayed = new NumberField({ rawValue: (data.totalPlayed?.getValue() ?? 0) - 1 });
        data.totalLosses = new NumberField({ rawValue: (data.totalLosses?.getValue() ?? 0) - 1 });
        if (data.totalPlayed.getValue() < 0 || data.totalLosses.getValue() < 0) {
            throw new Error('Unapplying loss delta would result in negative totalPlayed or totalLosses');
        }
    }
}

class RecordBonusUnlockDelta extends Delta {
    readonly type: DeltaTypes = DeltaTypes.RECORD_BONUS_UNLOCK;

    constructor(id?: string) {
        super(id);
    }

    apply(data: GameWideData) {
        data.numBonusPuzzlesUnlocked = new NumberField({
            rawValue: (data.numBonusPuzzlesUnlocked?.getValue() ?? 0) + 1,
        });
    }

    unapply(data: GameWideData) {
        data.numBonusPuzzlesUnlocked = new NumberField({
            rawValue: (data.numBonusPuzzlesUnlocked?.getValue() ?? 0) - 1,
        });
        if (data.numBonusPuzzlesUnlocked.getValue() < 0) {
            throw new Error('Unapplying bonus unlock delta would result in negative numBonusPuzzlesUnlocked');
        }
    }
}

const fieldConstructors = {
    lastVisit: DateField,
    lastVictory: DateField,
    streakLength: NumberField,
    maxStreakLength: NumberField,
    totalPlayed: NumberField,
    totalWins: NumberField,
    totalLosses: NumberField,
    scoreDistribution: ObjectField,
    difficultyLevel: DifficultyLevelField,
    timerEnabled: BooleanField,
    sessionId: StringField,
    consentGiven: BooleanField,
    userId: StringField,
    sessionIdCreationDate: DateField,
    seenFeedbackTutorial: BooleanField,
    seenAdvancedCarouselOptions: BooleanField,
    numBonusPuzzlesUnlocked: NumberField,
    seenShareIncentive: BooleanField,
    bonusPuzzlesSeen: NumberSetField,
    darkModeSetting: DarkModeSettingField,
    seenNativeAppPromotion: BooleanField,
    askedForRating: BooleanField,
    askedForReview: BooleanField,
    lastSignInAttempt: DateField,
};

export const ANONYMOUS_USER_ID = 'anonymous';

const fieldsWithDeltas = [
    'totalWins',
    'totalLosses',
    'totalPlayed',
    'scoreDistribution',
    'numBonusPuzzlesUnlocked',
] as const;

// Union of those keys
type DeltaKeys = (typeof fieldsWithDeltas)[number];
type UnsafeKeys = DeltaKeys | 'streakLength' | 'lastVictory';

export type FieldRegistry = typeof fieldConstructors;
export type SafeFieldRegistry = Omit<FieldRegistry, UnsafeKeys>;
type GameWideDataSchema = { [K in keyof FieldRegistry]: InstanceType<FieldRegistry[K]> };
type GameWideDataParams = { [K in keyof FieldRegistry]: FieldParamValue<K> };
export type GameWideData = Partial<GameWideDataSchema>;
export type FieldValue<K extends keyof FieldRegistry> = InstanceType<FieldRegistry[K]> extends Field<infer V>
    ? V
    : never;
export type FieldParamValue<K extends keyof FieldRegistry> = InstanceType<FieldRegistry[K]> extends FieldParams<infer V>
    ? FieldParams<V>
    : never;

export interface LocalRecordParams<U> {
    lastSyncedVersion?: number;
    lastModifiedMs?: number;
    userId: string;
    dirty?: boolean;
    data: U;
    deltas?: Array<{ type: DeltaTypes; turns?: number; id: string }>;
    deltasApplied?: Array<string>;
}

export abstract class SyncedRecord<T, U, SELF extends SyncedRecord<T, U, SELF>> {
    lastSyncedVersion?: number;
    lastModifiedMs: number = Date.now();
    userId: string = ANONYMOUS_USER_ID;
    dirty: boolean = true; // Local only
    dirtySincePush: boolean = true; // Local only
    data: T;
    deltas: Map<string, Delta> = new Map();
    deltasApplied: Set<string> = new Set();

    constructor(obj: LocalRecordParams<U>) {
        // Subclass is responsible for initializing data
        Object.assign(this, { ...obj, data: undefined });
        this.deltas = obj.deltas
            ? new Map<DeltaId, Delta>(
                  obj.deltas
                      .map((deltaObj) => DeltaFactory.from(deltaObj))
                      .filter((x) => x != null)
                      .map((delta) => [delta.id, delta])
              )
            : new Map<DeltaId, Delta>();
        this.deltasApplied = new Set(obj.deltasApplied ?? []);
    }

    // This is meant to be functionally symmetric when both records exist.
    merge(other: SELF | undefined): SELF {
        if (other == null) {
            return this.deepCopy();
        }
        if (this.userId !== other.userId) {
            throw new Error('Cannot merge LocalGameWideRecords with different userIds');
        }
        // Ack or Fast-forward merge: No local changes (non-dirty and no deltas to be cleaned up), so just take the record with the higher
        // last synced version, deferring to other in case of ties (which is fine, since they should
        // be identical in this case). Resulting record is clean and does not need to be pushed.
        if (
            this.lastSyncedVersion != null &&
            other.lastSyncedVersion != null &&
            !this.dirty &&
            !other.dirty &&
            this.deltas.size === 0 &&
            other.deltas.size === 0
        ) {
            // If both records are clean and have no outstanding deltas, just take the one with the higher lastSyncedVersion.
            return this.lastSyncedVersion <= other.lastSyncedVersion ? other.deepCopy() : this.deepCopy();
        }

        const merged: SELF = this.deepCopy();

        // Bring in all fields from other, merging as appropriate.
        // This ensures that if new top-level fields are added that this client doesn't know about,
        // we will simply carry them forward.
        Object.assign(merged, other.deepCopy());
        merged.data = this.mergeData(other);

        merged.lastSyncedVersion =
            this.lastSyncedVersion == null
                ? other.lastSyncedVersion
                : other.lastSyncedVersion == null
                ? this.lastSyncedVersion
                : Math.max(this.lastSyncedVersion, other.lastSyncedVersion);

        // Union deltas.
        merged.deltas = new Map<DeltaId, Delta>([...Array.from(this.deltas), ...Array.from(other.deltas)]);
        // Take the union of deltas applied. We never append to deltasApplied unless we know a speculative
        // write to the remote has succeeded, so this is safe.
        merged.deltasApplied = new Set([...this.deltasApplied, ...other.deltasApplied]);

        // Prune deltas that have already been applied - this is guaranteed to be safe
        for (const deltaKey of merged.deltas.keys()) {
            if (merged.deltasApplied.has(deltaKey)) {
                merged.deltas.delete(deltaKey);
                merged.deltasApplied.delete(deltaKey);
            }
        }

        merged.markDirty();
        merged.lastModifiedMs = Date.now();
        merged.userId = this.userId; // Arbitrary, they are both the same.

        return merged;
    }

    protected abstract mergeData(other: SELF): T;
    abstract saveLocally(store: KeyValueStore): Promise<void>;
    serialize(): LocalRecordParams<T> {
        return {
            ...this,
            deltas: Array.from(this.deltas.values()),
            deltasApplied: Array.from(this.deltasApplied),
        };
    }

    abstract deepCopy(): SELF;
    markClean() {
        this.dirty = false;
        this.dirtySincePush = false;
    }
    markCleanSincePush() {
        this.dirtySincePush = false;
    }
    markDirty(): void {
        this.lastModifiedMs = Date.now();
        this.dirty = true;
        this.dirtySincePush = true;
    }
    isDirty() {
        return this.dirty;
    }
    abstract applyAllDeltas(): void;
    abstract unapplyAllDeltas(): void;
    abstract getRemoteKey(): string;
}

export class PuzzleStateRecord extends SyncedRecord<PuzzleState, PuzzleStateParams, PuzzleStateRecord> {
    constructor(obj: LocalRecordParams<PuzzleStateParams>) {
        super(obj);
        this.data = PuzzleState.fromJSON(obj.data);
    }

    static createNew(puzzleState: PuzzleState, userId: string): PuzzleStateRecord {
        return new PuzzleStateRecord({ data: puzzleState, lastModifiedMs: Date.now(), dirty: true, userId });
    }

    deepCopy(): PuzzleStateRecord {
        return new PuzzleStateRecord(JSON.parse(JSON.stringify(this.serialize())));
    }

    copyWithNewData(newData: PuzzleState): PuzzleStateRecord {
        const copy = this.deepCopy();
        if (!deepEquals(copy.data.toJSON(), newData.toJSON())) {
            copy.lastModifiedMs = Date.now();
            copy.data = newData;
            copy.markDirty();
        }
        return copy;
    }

    copyWithUserId(newUserId: string): PuzzleStateRecord {
        const copy = this.deepCopy();
        copy.userId = newUserId;
        return copy;
    }

    saveLocally(store: KeyValueStore): Promise<void> {
        const key = new PuzzleKey(this.data.triple.id, this.userId).toString();
        return store.setValue(key, JSON.stringify(this.serialize()));
    }

    setPuzzleState(puzzleState: PuzzleState) {
        this.data = puzzleState;
        this.lastModifiedMs = Date.now();
        this.markDirty();
    }

    mergeData(other: PuzzleStateRecord): PuzzleState {
        // PuzzleState is one logical field
        const thisCopy: PuzzleState = this.deepCopy().data;
        const otherCopy: PuzzleState = other.deepCopy().data;

        // Completed puzzles beat in-progress puzzles.
        if (isCompleted(this.data.gameState) && !isCompleted(other.data.gameState)) {
            return thisCopy;
        } else if (isCompleted(other.data.gameState) && !isCompleted(this.data.gameState)) {
            return otherCopy;
        }

        // More progressed puzzles beat less progressed puzzles.
        if (otherCopy.attemptIdx == null) {
            return thisCopy;
        } else if (thisCopy.attemptIdx == null) {
            return otherCopy;
        } else if (thisCopy.attemptIdx > otherCopy.attemptIdx) {
            return thisCopy;
        } else if (thisCopy.attemptIdx < otherCopy.attemptIdx) {
            return otherCopy;
        }

        // Puzzles with more time elapsed beat puzzles with less time elapsed.
        if (otherCopy.timeElapsed == null) {
            return thisCopy;
        } else if (thisCopy.timeElapsed == null) {
            return otherCopy;
        } else if (thisCopy.timeElapsed > otherCopy.timeElapsed) {
            return thisCopy;
        } else if (thisCopy.timeElapsed < otherCopy.timeElapsed) {
            return otherCopy;
        }

        // More recently modified puzzles beat less recently modified puzzles.
        if (other.lastModifiedMs > this.lastModifiedMs) {
            return otherCopy;
        } else if (this.lastModifiedMs > other.lastModifiedMs) {
            return thisCopy;
        }

        // Easier-mode puzzles beat harder-mode puzzles.
        if (otherCopy.gameDifficultyLevel == null) {
            return thisCopy;
        } else if (thisCopy.gameDifficultyLevel == null) {
            return otherCopy;
        } else if (thisCopy.gameDifficultyLevel < otherCopy.gameDifficultyLevel) {
            return thisCopy;
        } else if (thisCopy.gameDifficultyLevel > otherCopy.gameDifficultyLevel) {
            return otherCopy;
        }

        // All else equal, return this one.
        return thisCopy;
    }

    applyAllDeltas(): void {
        // No deltas for puzzle state.
    }

    unapplyAllDeltas(): void {
        // No deltas for puzzle state.
    }

    getRemoteKey(): string {
        // We exclude userId because there is a separate userId field in the remote record
        return new PuzzleKey(this.data.triple.id).toString();
    }
}

export class RemoteGameRecord {
    version: number;
    lastModifiedMs: number;
    data: GameWideData;
}

export interface LocalGameWideRecordParams extends LocalRecordParams<Partial<GameWideDataParams>> {
    deltas?: Array<{ type: DeltaTypes; turns?: number; id: string }>;
    deltasApplied?: Array<string>;
}

type DeltaId = string;
export class LocalGameWideRecord extends SyncedRecord<GameWideData, Partial<GameWideDataParams>, LocalGameWideRecord> {
    constructor(obj: LocalRecordParams<Partial<GameWideDataParams>>) {
        super(obj);
        this.data = {
            // Ensure we carry forward any extra fields that may be added in the future.
            ...obj.data,
            lastVisit: obj?.data?.lastVisit ? DateField.create(obj.data.lastVisit) : undefined,
            lastVictory: obj?.data?.lastVictory ? DateField.create(obj.data.lastVictory) : undefined,
            streakLength: obj?.data?.streakLength ? NumberField.create(obj.data.streakLength) : undefined,
            maxStreakLength: obj?.data?.maxStreakLength ? NumberField.create(obj.data.maxStreakLength) : undefined,
            totalPlayed: obj?.data?.totalPlayed ? NumberField.create(obj.data.totalPlayed) : undefined,
            totalWins: obj?.data?.totalWins ? NumberField.create(obj.data.totalWins) : undefined,
            totalLosses: obj?.data?.totalLosses ? NumberField.create(obj.data.totalLosses) : undefined,
            scoreDistribution: obj?.data?.scoreDistribution
                ? ObjectField.create(obj.data.scoreDistribution)
                : undefined,
            difficultyLevel: obj?.data?.difficultyLevel
                ? DifficultyLevelField.create(obj.data.difficultyLevel)
                : undefined,
            timerEnabled: obj?.data?.timerEnabled ? BooleanField.create(obj.data.timerEnabled) : undefined,
            sessionId: obj?.data?.sessionId ? StringField.create(obj.data.sessionId) : undefined,
            consentGiven: obj?.data?.consentGiven ? BooleanField.create(obj.data.consentGiven) : undefined,
            userId: obj?.data?.userId ? StringField.create(obj.data.userId) : undefined,
            sessionIdCreationDate: obj?.data?.sessionIdCreationDate
                ? DateField.create(obj.data.sessionIdCreationDate)
                : undefined,
            seenFeedbackTutorial: obj?.data?.seenFeedbackTutorial
                ? BooleanField.create(obj.data.seenFeedbackTutorial)
                : undefined,
            seenAdvancedCarouselOptions: obj?.data?.seenAdvancedCarouselOptions
                ? BooleanField.create(obj.data.seenAdvancedCarouselOptions)
                : undefined,
            numBonusPuzzlesUnlocked: obj?.data?.numBonusPuzzlesUnlocked
                ? NumberField.create(obj.data.numBonusPuzzlesUnlocked)
                : undefined,
            seenShareIncentive: obj?.data?.seenShareIncentive
                ? BooleanField.create(obj.data.seenShareIncentive)
                : undefined,
            bonusPuzzlesSeen: obj?.data?.bonusPuzzlesSeen
                ? NumberSetField.create(obj.data.bonusPuzzlesSeen)
                : undefined,
            darkModeSetting: obj?.data?.darkModeSetting
                ? DarkModeSettingField.create(obj.data.darkModeSetting)
                : undefined,
            seenNativeAppPromotion: obj?.data?.seenNativeAppPromotion
                ? BooleanField.create(obj.data.seenNativeAppPromotion)
                : undefined,
            askedForRating: obj?.data?.askedForRating ? BooleanField.create(obj.data.askedForRating) : undefined,
            askedForReview: obj?.data?.askedForReview ? BooleanField.create(obj.data.askedForReview) : undefined,
            lastSignInAttempt: obj?.data?.lastSignInAttempt ? DateField.create(obj.data.lastSignInAttempt) : undefined,
        };
    }

    static fromLegacyFormat(rawLegacyState: RawLegacyState) {
        return new LocalGameWideRecord({
            // UserId is not stored in legacy format - this was before logins, so treat it as anonymous
            userId: ANONYMOUS_USER_ID,
            lastModifiedMs: Date.now(),
            dirty: true,
            data: {
                lastVisit: rawLegacyState.lastVisit ? { rawValue: rawLegacyState.lastVisit } : undefined,
                lastVictory: rawLegacyState.lastVictory ? { rawValue: rawLegacyState.lastVictory } : undefined,
                streakLength: rawLegacyState.streakLength
                    ? { rawValue: parseInt(rawLegacyState.streakLength, 10) }
                    : undefined,
                maxStreakLength: rawLegacyState.maxStreakLength
                    ? { rawValue: parseInt(rawLegacyState.maxStreakLength, 10) }
                    : undefined,
                totalPlayed: rawLegacyState.totalPlayed
                    ? { rawValue: parseInt(rawLegacyState.totalPlayed, 10) }
                    : undefined,
                totalWins: rawLegacyState.totalWins ? { rawValue: parseInt(rawLegacyState.totalWins, 10) } : undefined,
                totalLosses: rawLegacyState.totalLosses
                    ? { rawValue: parseInt(rawLegacyState.totalLosses, 10) }
                    : undefined,
                scoreDistribution: rawLegacyState.scoreDistribution
                    ? { rawValue: JSON.parse(rawLegacyState.scoreDistribution) as object }
                    : undefined,
                difficultyLevel: rawLegacyState.difficultyLevel
                    ? { rawValue: parseInt(rawLegacyState.difficultyLevel, 10) }
                    : undefined,
                timerEnabled: rawLegacyState.timerEnabled
                    ? { rawValue: rawLegacyState.timerEnabled === 'true' }
                    : undefined,
                sessionId: rawLegacyState.sessionId ? { rawValue: rawLegacyState.sessionId } : undefined,
                consentGiven: rawLegacyState.consentGiven
                    ? {
                          rawValue: rawLegacyState.consentGiven === 'true' || rawLegacyState.consentGiven == null,
                      }
                    : undefined,
                sessionIdCreationDate: rawLegacyState.sessionIdCreationDate
                    ? { rawValue: rawLegacyState.sessionIdCreationDate }
                    : undefined,
                seenFeedbackTutorial: rawLegacyState.seenFeedbackTutorial
                    ? { rawValue: rawLegacyState.seenFeedbackTutorial === 'true' }
                    : undefined,
                seenAdvancedCarouselOptions: rawLegacyState.seenAdvancedCarouselOptions
                    ? { rawValue: rawLegacyState.seenAdvancedCarouselOptions === 'true' }
                    : undefined,
                numBonusPuzzlesUnlocked: rawLegacyState.numBonusPuzzlesUnlocked
                    ? { rawValue: parseInt(rawLegacyState.numBonusPuzzlesUnlocked, 10) }
                    : undefined,
                seenShareIncentive: rawLegacyState.seenShareIncentive
                    ? { rawValue: rawLegacyState.seenShareIncentive === 'true' }
                    : undefined,
                bonusPuzzlesSeen: rawLegacyState.bonusPuzzlesSeen
                    ? {
                          rawValue: rawLegacyState.bonusPuzzlesSeen
                              .split(',')
                              .map((s) => parseInt(s, 10))
                              .filter((n) => !Number.isNaN(n)),
                      }
                    : undefined,
                darkModeSetting: rawLegacyState.darkModeSetting
                    ? { rawValue: parseInt(rawLegacyState.darkModeSetting, 10) }
                    : undefined,
                seenNativeAppPromotion: rawLegacyState.seenNativeAppPromotion
                    ? { rawValue: rawLegacyState.seenNativeAppPromotion === 'true' }
                    : undefined,
                askedForRating: rawLegacyState.askedForRating
                    ? { rawValue: rawLegacyState.askedForRating === 'true' }
                    : undefined,
                askedForReview: rawLegacyState.askedForReview
                    ? { rawValue: rawLegacyState.askedForReview === 'true' }
                    : undefined,
                lastSignInAttempt: rawLegacyState.lastSignInAttempt
                    ? { rawValue: rawLegacyState.lastSignInAttempt }
                    : undefined,
            },
        });
    }

    deepCopy(): LocalGameWideRecord {
        // For now serialize and deserialize to make a deep copy.
        return new LocalGameWideRecord(JSON.parse(JSON.stringify(this.serialize())) as LocalGameWideRecordParams);
    }

    static mergeBooleanFieldsTrueWins(
        field1: BooleanField | undefined,
        field2: BooleanField | undefined
    ): BooleanField | undefined {
        if (field1 == null) {
            return field2;
        }
        if (field2 == null) {
            return field1;
        }
        return new BooleanField({ rawValue: field1.getValue() || field2.getValue() });
    }

    static mergeBooleanFieldsFalseWins(
        field1: BooleanField | undefined,
        field2: BooleanField | undefined
    ): BooleanField | undefined {
        if (field1 == null) {
            return field2;
        }
        if (field2 == null) {
            return field1;
        }
        return new BooleanField({ rawValue: field1.getValue() && field2.getValue() });
    }

    static maxDates(date1: DateField | undefined, date2: DateField | undefined): DateField | undefined {
        if (date1 == null) {
            return date2;
        }
        if (date2 == null) {
            return date1;
        }
        return new DateField({
            rawValue:
                date1.getValue() > date2.getValue() ? date1.getValue().toISOString() : date2.getValue().toISOString(),
        });
    }

    static maxNumber(num1: NumberField | undefined, num2: NumberField | undefined): NumberField | undefined {
        if (num1 == null) {
            return num2;
        }
        if (num2 == null) {
            return num1;
        }
        return new NumberField({ rawValue: Math.max(num1.getValue(), num2.getValue()) });
    }

    static lastWriteWins<T extends Field<any, any>>(field1: T | undefined, field2: T | undefined): T | undefined {
        if (field1 == null) {
            return field2;
        }
        if (field2 == null) {
            return field1;
        }
        return field1.lastModifiedMs > field2.lastModifiedMs ? field1 : field2;
    }

    static union(field1: NumberSetField | undefined, field2: NumberSetField | undefined): NumberSetField | undefined {
        if (field1 == null) {
            return field2;
        }
        if (field2 == null) {
            return field1;
        }
        return new NumberSetField({
            rawValue: new Set([...(field1.getValue() ?? []), ...(field2.getValue() ?? [])]),
        });
    }

    static updateStreakData(
        merged: Partial<GameWideDataSchema>,
        record1: Partial<GameWideDataSchema>,
        record2: Partial<GameWideDataSchema>
    ): void {
        merged.maxStreakLength = LocalGameWideRecord.maxNumber(record1.maxStreakLength, record2.maxStreakLength);
        merged.lastVictory = LocalGameWideRecord.maxDates(record1.lastVictory, record2.lastVictory);
        if (record1.streakLength == null || record1.lastVictory == null) {
            merged.streakLength = record2.streakLength;
            return;
        }
        if (record2.streakLength == null || record2.lastVictory == null) {
            merged.streakLength = record1.streakLength;
            return;
        }
        const [maxRecord, minRecord] =
            record1.lastVictory.getValue().getTime() >= record2.lastVictory.getValue().getTime()
                ? [record1, record2]
                : [record2, record1];
        // We always use the max date as the last victory date.
        if (
            maxRecord.lastVictory == null ||
            minRecord.lastVictory == null ||
            maxRecord.streakLength == null ||
            minRecord.streakLength == null
        ) {
            throw new Error('Impossible null lastVictory in maxRecord or minRecord');
        }
        // The trickier question is how to merge the streak length. This depends on whether the streaks overlap in time.
        const minVictoryDate = new TZDate(new Date(minRecord.lastVictory.getValue()), TIME_ZONE);
        const minVictoryStreakLength = minRecord.streakLength.getValue();
        const maxVictoryDate = new TZDate(new Date(maxRecord.lastVictory.getValue()), TIME_ZONE);
        const maxVictoryStreakLength = maxRecord.streakLength.getValue();
        const firstDayOfRecentStreak = subDays(maxVictoryDate, maxVictoryStreakLength);
        const firstDayOfEarlierStreak = subDays(minVictoryDate, minVictoryStreakLength);
        if (differenceInCalendarDays(minVictoryDate, firstDayOfRecentStreak) >= 0) {
            // The streaks overlap or are adjacent, so we can "union" them.
            // Take the earliest possible start date, and the latest possible end date.
            const startDate = minDate([firstDayOfEarlierStreak, firstDayOfRecentStreak]);
            const endDate = maxVictoryDate;
            merged.streakLength = new NumberField({
                rawValue: differenceInCalendarDays(endDate, startDate),
            });
        } else {
            // The streaks do not overlap in time, so we take the more recent one.
            merged.streakLength = new NumberField({ rawValue: maxVictoryStreakLength });
        }
    }

    /* Preserves deltas - does not yet apply them. */
    mergeData(other: LocalGameWideRecord): Partial<GameWideDataSchema> {
        const mergedData = this.deepCopy().data;
        // Start by copying over all fields from other, to ensure that new fields that this client does not yet know about are preserved.
        Object.assign(mergedData, other.deepCopy().data);
        mergedData.askedForRating = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.askedForRating,
            other.data.askedForRating
        );
        mergedData.lastVisit = LocalGameWideRecord.maxDates(this.data.lastVisit, other.data.lastVisit);
        mergedData.lastVictory = LocalGameWideRecord.maxDates(this.data.lastVictory, other.data.lastVictory);

        LocalGameWideRecord.updateStreakData(mergedData, this.data, other.data);
        // Streak merging logic
        // If both streaks exist, and they overlap one another in time, then they can be merged.

        mergedData.askedForReview = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.askedForReview,
            other.data.askedForReview
        );
        // These values are monotonically increasing, so we take the max. There is no risk of not counting
        // local wins/losses because those are recorded as deltas and applied to the merged record.
        mergedData.totalWins = LocalGameWideRecord.maxNumber(this.data.totalWins, other.data.totalWins);
        mergedData.totalLosses = LocalGameWideRecord.maxNumber(this.data.totalLosses, other.data.totalLosses);
        mergedData.totalPlayed = LocalGameWideRecord.maxNumber(this.data.totalPlayed, other.data.totalPlayed);
        mergedData.scoreDistribution = new ObjectField({
            rawValue: Object.fromEntries(
                Array.from(
                    new Set([
                        ...Object.keys(this.data.scoreDistribution?.getValue() ?? {}),
                        ...Object.keys(other.data.scoreDistribution?.getValue() ?? {}),
                    ])
                ).map((key) => [
                    key,
                    Math.max(
                        (this.data.scoreDistribution?.getValue()?.[key] as number) ?? 0,
                        (other.data.scoreDistribution?.getValue()?.[key] as number) ?? 0
                    ),
                ])
            ),
        });
        mergedData.numBonusPuzzlesUnlocked = LocalGameWideRecord.maxNumber(
            this.data.numBonusPuzzlesUnlocked,
            other.data.numBonusPuzzlesUnlocked
        );

        // One-time switches: true wins
        mergedData.seenFeedbackTutorial = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.seenFeedbackTutorial,
            other.data.seenFeedbackTutorial
        );
        mergedData.seenNativeAppPromotion = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.seenNativeAppPromotion,
            other.data.seenNativeAppPromotion
        );
        mergedData.seenAdvancedCarouselOptions = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.seenAdvancedCarouselOptions,
            other.data.seenAdvancedCarouselOptions
        );
        mergedData.seenShareIncentive = LocalGameWideRecord.mergeBooleanFieldsTrueWins(
            this.data.seenShareIncentive,
            other.data.seenShareIncentive
        );

        // Preferences: last write wins
        mergedData.difficultyLevel = LocalGameWideRecord.lastWriteWins(
            this.data.difficultyLevel,
            other.data.difficultyLevel
        );
        mergedData.timerEnabled = LocalGameWideRecord.lastWriteWins(this.data.timerEnabled, other.data.timerEnabled);
        mergedData.darkModeSetting = LocalGameWideRecord.lastWriteWins(
            this.data.darkModeSetting,
            other.data.darkModeSetting
        );
        mergedData.consentGiven = LocalGameWideRecord.lastWriteWins(this.data.consentGiven, other.data.consentGiven);

        // Dates: last date wins
        mergedData.lastSignInAttempt = LocalGameWideRecord.maxDates(
            this.data.lastSignInAttempt,
            other.data.lastSignInAttempt
        );

        // Ephermeral data: last write wins
        mergedData.sessionId = LocalGameWideRecord.lastWriteWins(this.data.sessionId, other.data.sessionId);
        mergedData.sessionIdCreationDate = LocalGameWideRecord.lastWriteWins(
            this.data.sessionIdCreationDate,
            other.data.sessionIdCreationDate
        );

        // Collections: union
        mergedData.bonusPuzzlesSeen = LocalGameWideRecord.union(
            this.data.bonusPuzzlesSeen,
            other.data.bonusPuzzlesSeen
        );
        mergedData.userId = this.data.userId; // should be the same in both records

        return mergedData;
    }

    static emptyUserState(userId: string): LocalGameWideRecord {
        return new LocalGameWideRecord({
            userId: userId,
            lastModifiedMs: Date.now(),
            dirty: false,
            data: {},
        });
    }

    getInitialSnapshot(): GameWideData {
        // Return a copy that has all local deltas applied, so the client sees a locally consistent state.
        return this.copyWithDeltasApplied().data;
    }

    saveLocally(store: KeyValueStore): Promise<void> {
        return store.setValue(toGameWideStateKey(this.userId), JSON.stringify(this.serialize()));
    }

    private copyWithDeltasApplied(): LocalGameWideRecord {
        const copy = this.deepCopy();
        copy.applyAllDeltas();
        return copy;
    }

    applyAllDeltas(): void {
        for (const delta of this.deltas.values()) {
            if (!this.deltasApplied.has(delta.id)) {
                delta.apply(this.data);
                this.deltasApplied.add(delta.id);
            }
        }
    }

    unapplyAllDeltas(): void {
        for (const delta of this.deltas.values()) {
            if (this.deltasApplied.has(delta.id)) {
                delta.unapply(this.data);
                this.deltasApplied.delete(delta.id);
            }
        }
    }

    getField<K extends keyof FieldRegistry>(fieldName: K): FieldValue<K> | null {
        return (this.data[fieldName]?.getValue() as FieldValue<K>) ?? null;
    }

    setField<K extends keyof SafeFieldRegistry>(fieldName: K, value: FieldValue<K>): void {
        const ctor = fieldConstructors[fieldName] as unknown as new (
            obj: FieldParams<FieldValue<K>>
        ) => GameWideDataSchema[K];
        const newField = new ctor({ rawValue: value });
        if (
            this.data[fieldName] == null ||
            !deepEquals(this.data[fieldName].getSerializableValue(), newField.getSerializableValue())
        ) {
            this.data[fieldName] = newField;
            this.markDirty();
        }
    }

    updateStreakLocally(victoryDate: Date, streakLength: number): void {
        this.data.lastVictory = new DateField({ rawValue: victoryDate.toISOString() });
        this.data.streakLength = new NumberField({ rawValue: streakLength });
        this.markDirty();
    }

    recordWin(turns: number) {
        const delta = new RecordWinDelta(turns);
        this.deltas.set(delta.id, delta);
        this.markDirty();
    }

    recordLoss() {
        const delta = new RecordLossDelta();
        this.deltas.set(delta.id, delta);
        this.markDirty();
    }

    recordBonusUnlock() {
        const delta = new RecordBonusUnlockDelta();
        this.deltas.set(delta.id, delta);
        this.markDirty();
    }

    private static GAME_WIDE_STATE_REMOTE_KEY = 'game_wide_state';

    getRemoteKey(): string {
        return LocalGameWideRecord.GAME_WIDE_STATE_REMOTE_KEY;
    }

    static getRemoteKey(): string {
        return LocalGameWideRecord.GAME_WIDE_STATE_REMOTE_KEY;
    }
}

export const rawLocalStateNames = [
    'session',
    'notificationsEnabled',
    'notificationTime',
    'isFirstVisit',
    'signInCoolDownTimeRemaining',
    'lastProVerificationDate',
    'seenNotificationsSettings',
    'seenArchiveRewardAdPromo',
] as const;

export type RawLocalStateNames = (typeof rawLocalStateNames)[number];

export type RawLocalState = {
    [K in RawLocalStateNames]: string | null;
};

// Legacy, these are now stored in per-user GameWideData records, and they are
// only kept here for backwards compatibility.
export const rawLegacyStateNames = [
    'lastVisit',
    'lastVictory',
    'streakLength',
    'maxStreakLength',
    'totalPlayed',
    'totalWins',
    'totalLosses',
    'scoreDistribution',
    'difficultyLevel',
    'timerEnabled',
    'sessionId',
    'consentGiven',
    'userId',
    'sessionIdCreationDate',
    'seenFeedbackTutorial',
    'seenAdvancedCarouselOptions',
    'numBonusPuzzlesUnlocked',
    'seenShareIncentive',
    'bonusPuzzlesSeen',
    'darkModeSetting',
    'seenNativeAppPromotion',
    'askedForRating',
    'askedForReview',
    'lastSignInAttempt',
] as const;

export type RawLegacyStateNames = (typeof rawLegacyStateNames)[number];

export type RawLegacyState = {
    [K in RawLegacyStateNames]: string | null;
};

export {
    Guess,
    GuessLetterState,
    GameState,
    GuessTile,
    Directions,
    scoreToCssClass,
    TurnType,
    DifficultyLevel,
    ArchiveSelector,
    BonusSelector,
    InternalSelector,
    SpecialSelector,
    SelectedPuzzle,
    DarkModeSetting,
    isCompleted,
};

export interface PuzzleStateParams {
    readonly guesses?: GuessParams[];
    readonly gameState?: GameState;
    readonly cluesSynced?: boolean;
    readonly currentRow?: number;
    readonly currentCarouselIdx?: number;
    readonly triple: TripleParams;
    readonly attemptIdx?: number;
    readonly startTime?: number;
    readonly gameDifficultyLevel?: DifficultyLevel;
    readonly sessionId?: string;
    readonly letterPermutation?: number[];
    readonly score?: number;
    readonly solveOrder?: number[];
    readonly timeElapsed?: number; // in seconds
}

export class PuzzleState {
    readonly guesses?: Guess[];
    readonly gameState?: GameState;
    readonly cluesSynced?: boolean;
    readonly currentRow?: number;
    readonly currentCarouselIdx?: number;
    readonly triple: Triple;
    readonly attemptIdx?: number;
    readonly startTime?: number;
    readonly gameDifficultyLevel?: DifficultyLevel;
    readonly sessionId?: string;
    readonly letterPermutation?: number[];
    readonly score?: number;
    readonly solveOrder?: number[];
    readonly timeElapsed?: number; // in seconds

    constructor(params: {
        guesses?: Guess[];
        gameState?: GameState;
        cluesSynced?: boolean;
        currentRow?: number;
        currentCarouselIdx?: number;
        triple: Triple;
        attemptIdx?: number;
        startTime?: number;
        gameDifficultyLevel?: DifficultyLevel;
        sessionId?: string;
        letterPermutation?: number[];
        score?: number;
        solveOrder?: number[];
        timeElapsed?: number;
    }) {
        if (!params.triple) {
            throw new Error('Invalid PuzzleState params: ' + JSON.stringify(params));
        }
        // Ensure we carry forward any extra fields that may be added in the future.
        Object.assign(this, params);
    }

    copyWithTriple(newTriple: Triple): PuzzleState {
        return new PuzzleState({ ...this, triple: newTriple });
    }

    deepCopy(): PuzzleState {
        return PuzzleState.fromJSON(this.toJSON());
    }

    static fromJSON(json: string | PuzzleStateParams): PuzzleState {
        const obj: PuzzleStateParams = typeof json === 'string' ? (JSON.parse(json) as PuzzleStateParams) : json;
        if (!obj.triple) {
            throw new Error('PuzzleStateParams is missing triple: ' + JSON.stringify(json));
        }
        return new PuzzleState({
            // Ensure we carry forward any extra fields that may be added in the future.
            ...obj,
            triple: new Triple(obj.triple),
            guesses: obj.guesses?.map((guess: GuessParams) => Guess.from(guess)) ?? [
                new Guess(4),
                new Guess(5),
                new Guess(6),
            ],
        });
    }

    toJSON(): PuzzleStateParams {
        return {
            ...this,
            triple: this.triple.toJSON(),
            guesses: this.guesses?.map((guess) => guess.toJSON()),
        };
    }
}
